import React from 'react';
import { styled } from '@mui/material/styles';
import {
  ErrorQuestionDto,
  QuestionResponseDto,
  SectionResponseDto,
  SectionResponseUpdatedDto,
  SubSectionDto,
  SubSectionResponseDto,
} from 'models/dtoTypes';
import { AxiosResponse } from 'axios';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UpdateSubSectionDialog from 'dialogs/UpdateSubSectionDialog';
import GppGoodIcon from '@mui/icons-material/GppGood';
import ErrorIcon from '@mui/icons-material/Error';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';

const PREFIX = 'SubQuestionnaire';

const classes = {
  root: `${PREFIX}-root`,
};

const Root = styled('div')(() => ({
  [`&.${classes.root}`]: {},
}));

interface OnSubmitSubSectionResponse {
  (
    questionAlias: string,
    questionResponses: QuestionResponseDto[],
    subSectionId?: string,
  ): Promise<AxiosResponse<SectionResponseDto | ErrorQuestionDto> | void>;
}

interface OnDeleteFile {
  (fileId: string): void;
}

interface OnDeleteSubSectionResponse {
  (subSuctionAlias: string, subSectionResponseId: string): void;
}

interface OnUpdateSection {
  (sectionResponse: SectionResponseUpdatedDto): void;
}

interface Props {
  sectionAlias?: string;
  onSubmitSubSectionResponse?: OnSubmitSubSectionResponse;
  onDeleteSubSectionResponse?: OnDeleteSubSectionResponse;
  subSectionResponses?: SubSectionResponseDto[];
  onUpdateSection?: OnUpdateSection;
  itemsMax?: number;
  itemsMin?: number;
  name: string;
  label?: string;
  subSection?: SubSectionDto;
  onDeleteFile: OnDeleteFile;
}

function SubQuestionnaire(props: Props): JSX.Element {
  const { itemsMax, label, name, onDeleteFile, sectionAlias, subSection, subSectionResponses } =
    props;
  const itemsLength = subSectionResponses?.length || 0;
  // openSubSectionId: subSectionId | '': create | undefined: close
  const [openSubSectionId, setOpenSubSectionId] = React.useState<string | undefined>(undefined);
  const { t } = useTranslation('default');

  const handleOpen = (id: string) => {
    setOpenSubSectionId(id);
  };

  const handleClose = () => {
    setOpenSubSectionId(undefined);
  };

  const getFirstSubSectionResponse = (
    array?: SubSectionResponseDto[],
  ): SubSectionResponseDto | undefined => {
    return (array || []).find((s) => s.id === openSubSectionId);
  };

  const getSubSectionResponseName = (): string => {
    if (!openSubSectionId) return getPopupHeading(name);
    const subSectionResponse = (subSectionResponses || []).find((s) => s.id === openSubSectionId);
    return subSectionResponse?.name ?? 'Unknown';
  };

  function getPopupHeading(name: string) {
    let heading = '';
    switch (name) {
      case 'please-provide-a-list-of-your-current-directors':
        heading = 'Company Directors';
        break;
      case 'emissions-from-company-vehicle':
        heading = 'Company Vehicles';
        break;
      case 'emissions-from-purchased-goods-and-services':
        heading = 'Purchased Goods';
        break;
      case 'emissions-from-waste-disposal':
        heading = 'Waste';
        break;
      case 'emissions-from-fuel-combustion':
        heading = 'Fuel Combustion';
        break;
      case 'emissions-from-business-travel':
        heading = 'Business Travel';
        break;
      case 'emissions-from-employee-commuting':
        heading = 'Employee Commuting';
        break;
      case 'emissions-from-downstream-transport-and-distribution':
        heading = 'Transportation and Distribution';
        break;
      default:
        heading = 'Create';
        break;
    }
    return heading;
  }

  async function handleSave(questionResponses: QuestionResponseDto[]) {
    const { onSubmitSubSectionResponse } = props;
    const response = await (onSubmitSubSectionResponse &&
      onSubmitSubSectionResponse(name, questionResponses, openSubSectionId ?? undefined));
    setOpenSubSectionId(undefined);
    return response;
  }

  function handleDeleteSubSectionResponse(
    e: React.MouseEvent<HTMLElement>,
    subSectionResponseId: string,
  ) {
    e.stopPropagation();
    // DeleteSubSectionResponse
    const { onDeleteSubSectionResponse } = props;
    if (onDeleteSubSectionResponse && subSection?.alias) {
      onDeleteSubSectionResponse(subSection.alias, subSectionResponseId);
    }
  }

  const renderSingleSubSectionPromptText = (name: string, state: string) => {
    if (state === 'Completed') {
      return (
        <Stack direction="row" spacing={1}>
          <GppGoodIcon color="success" fontSize="medium" />
          <Typography variant="subtitle1" component="h6">
            {name}
          </Typography>
        </Stack>
      );
    } else {
      return (
        <Stack direction="row" spacing={1}>
          <ErrorIcon color="warning" fontSize="medium" />
          <Typography variant="subtitle1" component="h6">
            {name}
          </Typography>
        </Stack>
      );
    }
  };

  return (
    <Root className={classes.root}>
      {label}
      <List dense={false}>
        {(subSectionResponses || []).map((r) => (
          <ListItem key={r.id}>
            <ListItemText primary={renderSingleSubSectionPromptText(r.name || '', r.state || '')} />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                aria-label="edit"
                onClick={() => handleOpen(r.id || '')}
                size="large"
              >
                <Avatar>
                  <EditIcon />
                </Avatar>
              </IconButton>
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={(e) => handleDeleteSubSectionResponse(e, r.id || '')}
                size="large"
              >
                <Avatar>
                  <DeleteIcon />
                </Avatar>
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        {!itemsMax ||
          (itemsLength < itemsMax && (
            <ListItem>
              <ListItemText primary={t('add-a-new-answer')} />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleOpen('')}
                  size="large"
                >
                  <Avatar>
                    <AddIcon />
                  </Avatar>
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
      </List>
      <UpdateSubSectionDialog
        questionLabel={label}
        label={getSubSectionResponseName()}
        subSectionId={openSubSectionId}
        sectionAlias={sectionAlias}
        subSectionAlias={name}
        open={openSubSectionId !== undefined}
        subSection={subSection}
        onUpdateSection={props.onUpdateSection}
        subSectionResponse={getFirstSubSectionResponse(subSectionResponses)}
        onDeleteFile={onDeleteFile}
        onClose={handleClose}
        onSave={handleSave}
      />
    </Root>
  );
}

export default SubQuestionnaire;
